import { useQuery } from '@apollo/client';
import { useEffect, useMemo, useCallback } from 'react';
import { batch } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { analysisSelector } from '../../../redux/selectors/analyses';
import { isAnalysisLoadingSelector } from '../../../redux/selectors/loadingAnalyses';
import {
  populateAnalysisThunk,
  clearAnalysisAction,
} from '../../../redux/slices/analyses';
import { loadAnalysisAction } from '../../../redux/slices/loadingAnalyses';
import { GET_ANALYSIS } from '../../../utils/apollo/analysis';
import {
  IAnalysisQuery,
  IAnalysisQueryVariables,
} from '../../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../../utils/types';
import Chart from '../Chart';
import { ChartLoader } from '../ChartLoader';
import { ChartMode } from '../types';
import { theme } from '../../../styles/theme';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { reportViewed } from '../../../utils/analytics/events/report/events';

const DEFAULT_DISPLAY = 'Loading chart...';

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 400px;
  background: ${theme.colors.level0};
`;

const EmbeddableChart = ({ type }: { type: 'report' | 'cohort' }) => {
  const { reportId, cohortId } = useParams();
  const { track } = useAnalytics();

  const analysisId = type === 'report' ? reportId : cohortId;
  assertNotNil(analysisId);

  const mode = ChartMode.View;
  const dispatch = useAppDispatch();

  const loadAnalysisInStore = useCallback(
    ({ analysis }: IAnalysisQuery) => {
      assertNotNil(analysis);
      batch(() => {
        dispatch(populateAnalysisThunk({ analysis }));
        dispatch(loadAnalysisAction(analysis.id));
      });
      track(reportViewed(analysis, true));
    },
    [dispatch, track],
  );

  useQuery<IAnalysisQuery, IAnalysisQueryVariables>(GET_ANALYSIS, {
    variables: {
      analysisId,
      refresh: false,
    },
    onCompleted: loadAnalysisInStore,
  });

  const isAnalysisLoadingMemoSelector = useMemo(isAnalysisLoadingSelector, []);
  const currentAnalysisMemoizedSelector = useMemo(analysisSelector, []);

  const isAnalysisLoading = useAppSelector((state) =>
    isAnalysisLoadingMemoSelector(state, analysisId),
  );
  const currentAnalysis = useAppSelector((state) =>
    currentAnalysisMemoizedSelector(state, analysisId),
  );

  // Cleanup effect
  useEffect(() => {
    return () => {
      dispatch(clearAnalysisAction(analysisId));
    };
  }, [analysisId, dispatch]);

  if (isAnalysisLoading || !currentAnalysis) {
    return <ChartLoader mode={mode} display={DEFAULT_DISPLAY} />;
  }

  return (
    <Container>
      <Chart analysisId={analysisId} mode={ChartMode.View} isEmbedded={true} />
    </Container>
  );
};

export default EmbeddableChart;
